import type {Component, ComponentDefinition, ComponentModelDefinition, Editor, ToolbarButtonProps} from 'grapesjs';
import type {EventsHash} from 'backbone';
import type {StickerData} from '@Libraries/add-media-library';
import type {ImageItemSourceType} from '@Libraries/image-item.library';
import type {ComponentViewEvents, ImportComponentsOptions} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';

/**
 * GrapesJS accepts a plain `{event: handlerOrViewMethodName}` object for a component view's `events`
 * at runtime, but its bundled Backbone typings only allow a `() => EventsHash` of jQuery handlers.
 */
export function asViewEvents(events: ComponentViewEvents): () => EventsHash {
  return events as unknown as () => EventsHash;
}

export function matchByTagAndAttr(tag: string, attr: string, type: string): (el: HTMLElement) => {type: string} | false {
  return (el: HTMLElement): {type: string} | false => {
    if (el.tagName && el.tagName === tag && el.getAttribute(attr)) {
      return {type};
    }

    return false;
  };
}

export function defaultViewEvents(dispatchDblClickOnDoubleTap: (e: TouchEvent) => void): () => EventsHash {
  return asViewEvents({
    dblclick: 'onActive',
    touchend: dispatchDblClickOnDoubleTap,
  });
}

export function mediaViewEvents(
  dispatchDblClickOnDoubleTap: (e: TouchEvent) => void,
  mediaHoverEventHandlers: {
    mouseenter: (event: MouseEvent) => void;
    mouseleave: (event: MouseEvent) => void;
  }
): () => EventsHash {
  return asViewEvents({
    dblclick: 'onActive',
    touchend: dispatchDblClickOnDoubleTap,
    ...mediaHoverEventHandlers,
  });
}

/** Everything the per-domain register-* functions need, assembled once in the orchestrator and passed by reference. */
export interface ComponentRegistrationContext {
  editor: Editor;
  options: ImportComponentsOptions;
  toolbarContainerBtn: Partial<ToolbarButtonProps>;
  toolbarMoveBtn: Partial<ToolbarButtonProps>;
  toolbarMergeTagsBtn: Partial<ToolbarButtonProps>;
  toolbarAiRewriteBtn: Partial<ToolbarButtonProps>;
  defaultToolbarOptions: Partial<ToolbarButtonProps>[];
  /** Shared `UNSTYLABLE_EMAIL_STYLES` array — GrapesJS mutates it in place. */
  unstylableEmailStyles: string[];
  dispatchDblClickOnDoubleTap: (e: TouchEvent) => void;
  mediaHoverEventHandlers: {
    mouseenter: (event: MouseEvent) => void;
    mouseleave: (event: MouseEvent) => void;
  };
  mediaClickEventHandler: (event: MouseEvent) => void;
  openChangeImageModal: () => void;
  mediaResizeOpts: ComponentDefinition['resizable'];
  isDroppableInRow: (target: Component, destination: Component) => boolean;
  getDraggableForColumn: () => string;
  getStickerSource: (stickers: StickerData[]) => ImageItemSourceType | undefined;
  initRemovalOfUnsupportedCssProperties: (component: Component) => void;
  /** `this` is bound to the GrapesJS component view. */
  onTextTouchEnd: (this: ComponentModelDefinition, e: TouchEvent) => void;
  buttonToolbarOptions: Partial<ToolbarButtonProps>[];
  textToolbarOptions: Partial<ToolbarButtonProps>[];
}
